import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface BookableSlot {
  startAt: string
  endAt: string
}

interface SlotsResult {
  ok: boolean
  code?: string
  slots?: { start_at: string; end_at: string }[]
}

/**
 * Free bookable slots for one relationship's instructor, over a date range, via
 * get_bookable_slots() (migration 093; migration 098 added p_duration_minutes) -- the
 * only read path either the student or the instructor has into the instructor's
 * configured availability; never the raw windows/exceptions tables directly. Each gap in
 * the instructor's availability is subdivided into actual durationMinutes-long candidate
 * start times (stepped every 30 minutes), not offered as one giant block spanning the
 * whole free window.
 */
export function useBookableSlots(relationshipId: string | null, fromDate: string | null, toDate: string | null, durationMinutes: number = 60) {
  const [slots, setSlots] = useState<BookableSlot[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSlots = useCallback(async () => {
    if (!relationshipId || !fromDate || !toDate) { setSlots([]); return }
    setLoading(true)
    setError(null)
    try {
      const { data, error: rpcError } = await supabase.rpc('get_bookable_slots', {
        p_relationship_id: relationshipId,
        p_from_date: fromDate,
        p_to_date: toDate,
        p_duration_minutes: durationMinutes,
      })
      if (rpcError) { setError('Could not load availability.'); return }
      const result = data as unknown as SlotsResult
      if (!result.ok) { setError('Could not load availability.'); return }
      setSlots((result.slots ?? []).map(s => ({ startAt: s.start_at, endAt: s.end_at })))
    } finally {
      setLoading(false)
    }
  }, [relationshipId, fromDate, toDate, durationMinutes])

  useEffect(() => { fetchSlots() }, [fetchSlots])

  return { slots, loading, error, refresh: fetchSlots }
}
